import { Link } from 'react-router-dom';
import styles from './index.module.scss';
import SiteTopNav from '@/components/SiteTopNav';

const HowHardwoodMade = () => {
  return (
    <div className={styles['article-page']}>
      <div className={styles.page}>
        <header className={styles['article-header']}>
          <SiteTopNav variant="light" />
        </header>

        <main className={styles['article-main']}>
          <Link className={styles['article-back']} to="/academy/hardwood-basics">
            ← Back to Flooring 101
          </Link>

          <p className={styles['article-eyebrow']} data-motion="copy">
            Sakura Floors Academy
          </p>

          <h1 className={styles['article-title']} data-motion="hero">
            How Engineered Hardwood Is Made
          </h1>

          <p className={styles['article-intro']} data-motion="copy">
            Engineered hardwood flooring combines genuine hardwood with advanced manufacturing to
            create a floor that offers natural beauty, dimensional stability, and long-term
            performance.
          </p>

          <p className={styles['article-intro']} data-motion="copy">
            From selecting premium lumber to applying the final protective finish, every stage of
            production plays an important role in the appearance, durability, and performance of the
            finished plank.
          </p>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Step 1</p>

            <h2 className={styles['article-section__title']}>Selecting Premium Hardwood</h2>

            <p className={styles['article-section__text']}>
              Every engineered hardwood floor begins with carefully selected hardwood.
            </p>

            <p className={styles['article-section__text']}>
              The top layer, known as the wear layer, is genuine hardwood—not a printed image or
              imitation surface. This is the part of the floor you see, touch, and live on every
              day.
            </p>

            <h3 className={styles['article-section__subtitle']}>Common Hardwood Species</h3>

            <ul className={styles['article-list']}>
              <li>White Oak</li>
              <li>Japanese Oak</li>
              <li>European Oak</li>
              <li>Ash</li>
              <li>Walnut</li>
              <li>Hickory</li>
              <li>Maple</li>
            </ul>

            <p className={styles['article-section__text']}>
              Each species offers its own grain pattern, color variation, hardness, and natural
              character.
            </p>

            <p className={styles['article-section__text']}>
              Premium manufacturers inspect and sort the lumber before production, selecting boards
              for beauty, consistency, and structural quality. Depending on the collection, the wood
              may also be graded as Prime, Select, or Character.
            </p>

            <h3 className={styles['article-section__subtitle']}>Why It Matters</h3>

            <p className={styles['article-section__text']}>The hardwood wear layer determines:</p>

            <ul className={styles['article-list']}>
              <li>Natural grain pattern</li>
              <li>Color and tonal variation</li>
              <li>Character and knot appearance</li>
              <li>The overall visual style of the floor</li>
              <li>
                Whether the floor may be refinished in the future, depending on the wear-layer
                thickness
              </li>
            </ul>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Step 2</p>

            <h2 className={styles['article-section__title']}>Building the Engineered Core</h2>

            <p className={styles['article-section__text']}>
              Once the hardwood surface is prepared, it is bonded to a multi-layer plywood core.
            </p>

            <p className={styles['article-section__text']}>
              Unlike solid hardwood, engineered flooring is constructed with multiple wood layers
              arranged in alternating grain directions. This cross-layer construction helps reduce
              the expansion and contraction caused by changes in temperature and humidity.
            </p>

            <p className={styles['article-section__text']}>
              Each layer balances the movement of the layers around it, helping the finished plank
              remain flatter and more dimensionally stable throughout changing seasons.
            </p>

            <p className={styles['article-section__text']}>
              High-quality engineered flooring often uses premium hardwood plywood or eucalyptus
              plywood because of its strength, consistency, and moisture resistance.
            </p>

            <h3 className={styles['article-section__subtitle']}>
              Why Cross-Layer Construction Matters
            </h3>

            <ul className={styles['article-list']}>
              <li>Greater dimensional stability</li>
              <li>Reduced seasonal movement</li>
              <li>Improved resistance to warping</li>
              <li>Better performance over concrete subfloors</li>
              <li>Compatibility with many radiant heating systems</li>
              <li>The ability to manufacture wider and longer planks</li>
            </ul>

            <p className={styles['article-section__text']}>
              This engineered structure is one of the primary reasons engineered hardwood is widely
              selected for modern luxury homes.
            </p>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Step 3</p>

            <h2 className={styles['article-section__title']}>Precision Milling</h2>

            <p className={styles['article-section__text']}>
              After the plank is assembled, each board is precision milled using advanced
              manufacturing equipment.
            </p>

            <p className={styles['article-section__text']}>
              Tongue-and-groove or click-lock profiles are carefully machined into the edges of the
              plank so neighboring boards fit together accurately during installation.
            </p>

            <p className={styles['article-section__text']}>
              Tight manufacturing tolerances help installers create a smooth, aligned floor with
              minimal visible gaps.
            </p>

            <h3 className={styles['article-section__subtitle']}>Benefits of Precision Milling</h3>

            <ul className={styles['article-list']}>
              <li>Tight-fitting joints</li>
              <li>Cleaner installation</li>
              <li>Better plank alignment</li>
              <li>Improved structural performance</li>
              <li>Reduced movement after installation</li>
            </ul>

            <p className={styles['article-section__text']}>
              Even small improvements in manufacturing precision can significantly improve the
              appearance and longevity of the finished floor.
            </p>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Step 4</p>

            <h2 className={styles['article-section__title']}>Creating the Surface Texture</h2>

            <p className={styles['article-section__text']}>
              Before finishing, the hardwood surface receives its final texture.
            </p>

            <p className={styles['article-section__text']}>
              Surface treatments enhance the natural grain while adding depth, character, and
              tactile appeal.
            </p>

            <div className={styles['article-feature-list']}>
              <div className={styles['article-feature']}>
                <h3 className={styles['article-feature__title']}>Wire Brushed</h3>
                <p className={styles['article-feature__text']}>
                  Steel brushes remove the softer portions of the grain, revealing natural texture
                  while helping conceal minor everyday scratches.
                </p>
              </div>

              <div className={styles['article-feature']}>
                <h3 className={styles['article-feature__title']}>Light Wire Brushed</h3>
                <p className={styles['article-feature__text']}>
                  A subtler brushing process that preserves a smoother appearance while adding
                  gentle texture.
                </p>
              </div>

              <div className={styles['article-feature']}>
                <h3 className={styles['article-feature__title']}>Hand Scraped</h3>
                <p className={styles['article-feature__text']}>
                  Artisan-inspired detailing creates a traditional handcrafted appearance with
                  distinctive visual character.
                </p>
              </div>

              <div className={styles['article-feature']}>
                <h3 className={styles['article-feature__title']}>Smooth</h3>
                <p className={styles['article-feature__text']}>
                  A refined, contemporary surface that highlights the wood’s natural grain and clean
                  lines.
                </p>
              </div>
            </div>

            <p className={styles['article-section__text']}>
              Each texture creates a different design aesthetic, allowing homeowners and designers
              to achieve anything from rustic warmth to modern minimalism.
            </p>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Step 5</p>

            <h2 className={styles['article-section__title']}>Applying the Protective Finish</h2>

            <p className={styles['article-section__text']}>
              The final manufacturing stage is the application of the protective finish.
            </p>

            <p className={styles['article-section__text']}>
              Premium engineered hardwood commonly receives multiple coats of UV-cured finish. Each
              coat is cured using ultraviolet light, creating a durable protective surface while
              preserving the natural appearance of the wood.
            </p>

            <h3 className={styles['article-section__subtitle']}>Benefits of UV-Cured Finishes</h3>

            <ul className={styles['article-list']}>
              <li>Enhanced scratch resistance</li>
              <li>Improved stain protection</li>
              <li>Easier routine maintenance</li>
              <li>Better resistance to fading</li>
              <li>Long-lasting durability</li>
              <li>A natural, low-sheen appearance</li>
            </ul>

            <p className={styles['article-section__text']}>
              With proper care, a quality finish can help maintain the floor’s appearance and
              performance for many years.
            </p>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Final Stage</p>

            <h2 className={styles['article-section__title']}>Quality Control</h2>

            <p className={styles['article-section__text']}>
              Before leaving the factory, every plank undergoes a detailed quality inspection.
            </p>

            <p className={styles['article-section__text']}>Manufacturers evaluate:</p>

            <ul className={styles['article-list']}>
              <li>Plank dimensions</li>
              <li>Moisture content</li>
              <li>Milling accuracy</li>
              <li>Surface finish</li>
              <li>Color consistency</li>
              <li>Structural integrity</li>
            </ul>

            <p className={styles['article-section__text']}>
              Only planks that meet the manufacturer’s standards proceed to packaging and shipment.
              This attention to detail helps ensure consistent appearance, installation quality, and
              long-term performance.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default HowHardwoodMade;
